import { useEffect, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { toORPCError } from '@orpc/client'
import { toast } from 'sonner'
import { ShieldAlert } from 'lucide-react'
import { MCP_MAX_SERVERS_PER_PROJECT, MCP_NAME_MAX, type McpServer } from '@hub/contracts'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { orpc } from '@/lib/orpc-client'
import { settingsInputClass } from './settings-layout'

/**
 * Add MCP (board wwfu): name, URL, optional API key, and — before anything
 * leaves HUB — the shared-identity warning (PLAN D6). Submit probes the
 * server: an open or API-key server is added straight away; an OAuth server
 * sends the browser to its consent page and comes back to the Plugins pane.
 */
export function AddMcpDialog({
  projectId,
  open,
  onOpenChange,
  onCreated,
}: {
  projectId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (server: McpServer) => void
}) {
  const { t } = useTranslation('settings')
  const [name, setName] = useState('')
  const [url, setUrl] = useState('')
  const [withKey, setWithKey] = useState(false)
  const [apiKey, setApiKey] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setName('')
    setUrl('')
    setWithKey(false)
    setApiKey('')
    setError(null)
  }, [open])

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      const result = await orpc.mcp.create({
        projectId,
        name: name.trim(),
        url: url.trim(),
        ...(withKey && apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
      })
      if (result.authorizeUrl) {
        // Leave for the server's consent page; the callback returns to this pane.
        window.location.assign(result.authorizeUrl)
        return
      }
      onCreated(result.server)
      onOpenChange(false)
      if (result.server.status === 'active') toast.success(t('plugins.connected'))
    } catch (err) {
      setError(describeError(err, t))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-slot="add-mcp-dialog">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{t('plugins.dialog.title')}</DialogTitle>
            <DialogDescription>{t('plugins.dialog.description')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-1.5">
            <label htmlFor="mcp-name" className="text-sm font-medium">
              {t('plugins.dialog.name')}
            </label>
            <input
              id="mcp-name"
              className={settingsInputClass}
              value={name}
              maxLength={MCP_NAME_MAX}
              placeholder={t('plugins.dialog.namePlaceholder')}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="mcp-url" className="text-sm font-medium">
              {t('plugins.dialog.url')}
            </label>
            <input
              id="mcp-url"
              className={`${settingsInputClass} font-mono`}
              value={url}
              inputMode="url"
              autoComplete="off"
              spellCheck={false}
              placeholder={t('plugins.dialog.urlPlaceholder')}
              onChange={(e) => setUrl(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                data-slot="mcp-api-key-toggle"
                checked={withKey}
                onChange={(e) => setWithKey(e.target.checked)}
                className="size-4 accent-foreground"
              />
              {t('plugins.dialog.apiKeyToggle')}
            </label>
            {withKey && (
              <div className="space-y-1.5 pl-6">
                <label htmlFor="mcp-key" className="text-sm font-medium">
                  {t('plugins.dialog.apiKey')}
                </label>
                <input
                  id="mcp-key"
                  type="password"
                  autoComplete="off"
                  className={`${settingsInputClass} font-mono`}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">{t('plugins.dialog.apiKeyHint')}</p>
              </div>
            )}
          </div>

          <div
            data-slot="mcp-identity-warning"
            className="flex gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-950 dark:text-amber-100"
          >
            <ShieldAlert className="mt-0.5 size-4 shrink-0" />
            <div className="space-y-1">
              <p className="font-medium">{t('plugins.dialog.identityTitle')}</p>
              <p>{t('plugins.dialog.identity')}</p>
              <p className="text-xs opacity-80">{t('plugins.dialog.oauthNote')}</p>
            </div>
          </div>

          {error && (
            <p className="text-sm text-destructive" data-slot="mcp-add-error" role="alert">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('plugins.cancel')}
            </Button>
            <Button type="submit" disabled={submitting || !name.trim() || !url.trim()}>
              {submitting ? t('plugins.dialog.submitting') : t('plugins.dialog.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function describeError(err: unknown, t: TFunction<'settings'>) {
  const e = toORPCError(err)
  const data = (e.data ?? {}) as { reason?: string; probeReason?: string; message?: string }
  switch (data.reason) {
    case 'INVALID_URL':
      return t('plugins.dialog.errors.INVALID_URL')
    case 'LIMIT_REACHED':
      return t('plugins.dialog.errors.LIMIT_REACHED', { max: MCP_MAX_SERVERS_PER_PROJECT })
    case 'NOT_CONFIGURED':
      return t('plugins.dialog.errors.NOT_CONFIGURED')
    case 'PROBE_FAILED':
      return data.probeReason === 'unauthorized'
        ? t('plugins.dialog.errors.unauthorized', { message: data.message ?? '' })
        : t('plugins.dialog.errors.PROBE_FAILED', { message: data.message ?? e.message })
  }
  if (e.code === 'FORBIDDEN') return t('plugins.dialog.errors.FORBIDDEN')
  return t('plugins.dialog.errors.generic')
}
